import React, { type FunctionComponent, type ReactNode, useMemo } from 'react'
import styled from '@emotion/styled'
import { Flex } from 'legacy/dash-blocks/flex/Flex'
import { type SpacingUnits } from 'legacy/dash-blocks/foundation/spacing-units/spacingUnits.types'
import { dashColors } from 'legacy/dash-blocks/foundation/colors'

export const DASH_FRAME_HEADER_HEIGHT: SpacingUnits = 60
export const DASH_FRAME_SIDEBAR_WIDTH: SpacingUnits = 224
export const DASH_FRAME_CONTENT_BAR_HEIGHT: SpacingUnits = 56

const FrameOuterContainer = styled(Flex)`
  height: 100vh;
  max-height: 100vh;
`

const FrameInnerContainer = styled(Flex)`
  max-width: 100%;
  flex-grow: 1;
`

const FrameContentContainer = styled(Flex)`
  overflow: hidden;
  flex-grow: 1;
`

const FrameHeader = styled(Flex)`
  height: ${DASH_FRAME_HEADER_HEIGHT}px;
  flex: 0 0 auto;
`

const FrameSidebar = styled(Flex)`
  width: ${DASH_FRAME_SIDEBAR_WIDTH}px;
  flex: 0 0 auto;
`

const FrameContentBar = styled(Flex)`
  height: ${DASH_FRAME_CONTENT_BAR_HEIGHT}px;
  flex: 0 0 auto;
`

const FrameContentBody = styled(Flex)`
  flex: 1 1 auto;
`

interface Props {
  header?: ReactNode
  sidebar?: ReactNode
  contentBar?: ReactNode
  contentBody?: ReactNode
  fixedHeight?: boolean
}

export const DashFrame: FunctionComponent<Props> = ({
  header,
  sidebar,
  contentBar,
  contentBody,
  fixedHeight,
}) => {
  const renderedHeader = header && (
    <FrameHeader
      direction="row"
      wrap="nowrap"
      fillWidth
      overrideStyle={{ backgroundColor: dashColors.WHITE }}
    >
      {header}
    </FrameHeader>
  )

  const renderedSidebar = sidebar && (
    <FrameSidebar
      direction="column"
      wrap="nowrap"
      fillHeight
      padding={['xxsmall', 'xxsmall', 'none', 'none']}
      overrideStyle={{ backgroundColor: dashColors.WHITE }}
    >
      {sidebar}
    </FrameSidebar>
  )

  const renderedContentBar = contentBar && (
    <FrameContentBar
      direction="row"
      fillWidth
      wrap="nowrap"
      align="center"
      padding={['xxsmall', 'none']}
      overrideStyle={{ backgroundColor: dashColors.WHITE }}
    >
      {contentBar}
    </FrameContentBar>
  )

  const contentBodyHeight = useMemo(() => {
    if (!fixedHeight) return undefined

    const subtraction =
      (renderedHeader ? DASH_FRAME_HEADER_HEIGHT : 0) +
      (contentBar ? DASH_FRAME_CONTENT_BAR_HEIGHT : 0)

    return `calc(100vh - ${subtraction}px)`
  }, [contentBar, fixedHeight, renderedHeader])

  const renderedContentBody = contentBody && (
    <FrameContentBody
      direction="column"
      wrap="nowrap"
      fillWidth
      overrideStyle={{
        height: contentBodyHeight,
      }}
    >
      {contentBody}
    </FrameContentBody>
  )

  return (
    <FrameOuterContainer direction="column" wrap="nowrap" fillWidth>
      {renderedHeader}
      <FrameInnerContainer direction="row" wrap="nowrap" fillWidth>
        {renderedSidebar}
        <FrameContentContainer direction="column" wrap="nowrap" fillHeight>
          {renderedContentBar}
          {renderedContentBody}
        </FrameContentContainer>
      </FrameInnerContainer>
    </FrameOuterContainer>
  )
}
